import React from 'react';
import MaterialTable, { MTableToolbar } from 'material-table';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Container, Typography } from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import Grid from '@mui/material/Grid2';
import { PhioengagePatientProfilev2 } from './types';
import Filter, { MultiFilter } from 'views/triage/components/Filter';
import { USE_CASE_COLORS, USE_CASE_LIST } from './PhioengagePatientList';
import { AGREEMENT_TYPE, ClinicalServicesClient } from 'components/ClinicalServicesDropdown';
import { firestoreInstance } from 'config/ApiService';
import ListTaskOwnerComponent from './components/ListTaskOwnerComponent';
import { ActionPanel } from './components/messageAction';
import { dataQuery } from 'utils/remote-fetch';
import { logGoogleAnalyticsEvent } from 'utils/gaLog';
import { setUserActivity } from 'services/trackUserActivity';

const PhioengageWorklist = () => {
    const [filterList, setFilterList] = React.useState<ClinicalServicesClient[]>([]);
    const client = useSelector((state: RootState) => state.AuthReducer.organisation);
    const clinician = useSelector((state: RootState) => state.AuthReducer.user);
    const permissions = useSelector((state: RootState) => state.AuthReducer.permissions);

    // filters
    const [clientFilter, setClientFilter] = React.useState<string[]>(['all']);
    const [useCaseFilter, setUseCaseFilter] = React.useState<string[]>(['all']);
    const [taskOwnerFilter, setTaskOwnerFilter] = React.useState<string>('all');
    const [pendingTaskFilter, setPendingTaskFilter] = React.useState<string[]>(['all']);

    const navigate = useNavigate();

    const { enqueueSnackbar } = useSnackbar();

    const mtTableRef = React.useRef<any>(null);

    const isInternalClinicalServices = permissions.includes('internal_clinical_services');

    const applyCustomFilterToResults = (data: any[]) => {
        if (isInternalClinicalServices && useCaseFilter.length && useCaseFilter.includes('all')) {
            const clientIds = filterList.map((c) => c.id);
            // filter the data based on the clientIds
            return data.filter((item) => clientIds.includes(item.customer_id));
        }
        return data;
    };

    const buildQueryConstraints = React.useCallback(() => {
        const qConstraints: { name: string; op: string; value: any }[] = [];
        if (isInternalClinicalServices && useCaseFilter.length && !useCaseFilter.includes('all')) {
            qConstraints.push({ name: 'customer_preset', op: 'in', value: useCaseFilter });
        }
        if (
            isInternalClinicalServices &&
            clientFilter &&
            clientFilter.length > 0 &&
            !clientFilter?.includes('all')
        ) {
            qConstraints.push({ name: 'customer_id', op: 'in', value: clientFilter });
        }
        if (!isInternalClinicalServices) {
            qConstraints.push({ name: 'customer_id', op: '==', value: client.id });
        }
        if (taskOwnerFilter !== 'all' && taskOwnerFilter) {
            qConstraints.push({
                name: 'clinician_id',
                op: '==',
                value: taskOwnerFilter
            });
        }
        if (
            pendingTaskFilter &&
            pendingTaskFilter.length > 0 &&
            !pendingTaskFilter.includes('all')
        ) {
            // lets remove unread_messages_count from the pendingTaskFilter
            const taskFilterWithoutUnread = pendingTaskFilter.filter(
                (filter) => filter !== 'unread_messages_count'
            );
            // if the taskFilterWithoutUnread is empty, we will not add the actionable_events filter
            if (taskFilterWithoutUnread.length > 0) {
                qConstraints.push({
                    name: 'actionable_events',
                    op: 'array-contains-any',
                    value: taskFilterWithoutUnread
                });
            }
        }
        // pick a range of task_sort_priority to exclude patients with the negligible priority of -1
        qConstraints.push({ name: 'task_sort_priority', op: '>=', value: 1 });
        qConstraints.push({ name: 'task_sort_priority', op: '<=', value: 3 });
        // lower selectivity fields
        if (pendingTaskFilter.includes('unread_messages_count')) {
            qConstraints.push({
                name: 'unread_messages_count',
                op: '>',
                value: 0
            });
        }
        qConstraints.push({ name: 'task_sort_priority', op: 'orderBy', value: 'desc' });
        return qConstraints;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        clientFilter,
        useCaseFilter,
        taskOwnerFilter,
        pendingTaskFilter,
        filterList,
        isInternalClinicalServices
    ]);

    React.useEffect(() => {
        (async () => {
            const ref = collection(firestoreInstance(), 'clients');
            const q = query(
                ref,
                where('agreement_type', 'in', [
                    AGREEMENT_TYPE.ENGAGE,
                    AGREEMENT_TYPE.ACCESS_AND_ENGAGE,
                    AGREEMENT_TYPE.ACCESS_ENGAGE_AND_COLLECT
                ]),
                where('clinical_services_enabled', '==', true)
            );
            const snapshots = await getDocs(q);
            const clients: ClinicalServicesClient[] = [];
            snapshots.forEach((doc) => {
                const data = doc.data();
                clients.push({
                    id: doc.id,
                    name: data.name,
                    agreement_type: data.agreement_type
                });
            });
            clients.sort((a, b) => a.name.localeCompare(b.name));
            clients.unshift({
                id: 'all',
                name: 'All'
            } as ClinicalServicesClient);
            setFilterList(clients);
        })();
    }, []);

    React.useEffect(() => {
        if (mtTableRef.current) {
            // eslint-disable-next-line no-unused-expressions
            mtTableRef.current.onQueryChange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mtTableRef.current, clientFilter, useCaseFilter, taskOwnerFilter, pendingTaskFilter]);

    // Log Page View
    React.useEffect(() => {
        import('utils/gaLog').then(({ logScreenView }) => {
            logScreenView('clinical_worklist_page_view');
        });
    }, []);

    return (
        <Box minWidth="650px">
            <MaterialTable
                title={`Clinical Worklist - ${
                    isInternalClinicalServices
                        ? 'All Internal Clinical Service Customers'
                        : client.name
                }`}
                data={(query) => {
                    return dataQuery(query, {
                        remoteCollectionName: 'phioengage_user_profiles',
                        fields: buildQueryConstraints(),
                        customSort: applyCustomFilterToResults
                    });
                }}
                tableRef={mtTableRef}
                columns={[
                    {
                        title: 'Tasks',
                        field: 'id',
                        sorting: false,
                        render: (rowData: any) => {
                            return <ActionPanel data={rowData} />;
                        }
                    },
                    {
                        title: 'UID',
                        field: 'id',
                        sorting: false,
                        render: (rowData: any) => (
                            <Typography variant="body2">{rowData.id}</Typography>
                        )
                    },
                    {
                        title: 'Patient',
                        field: 'first_name',
                        sorting: false,
                        render: (rowData: any) => (
                            <Typography variant="body2" color="textSecondary">
                                {rowData.first_name + ' ' + rowData?.last_name}
                            </Typography>
                        )
                    },
                    {
                        title: 'Owner',
                        field: 'task_owner',
                        sorting: false,
                        render: (rowData: any) => {
                            return (
                                <ListTaskOwnerComponent
                                    item={rowData as unknown as PhioengagePatientProfilev2}
                                    callback={() => {
                                        enqueueSnackbar('Task owner updated successfully', {
                                            variant: 'success'
                                        });
                                    }}
                                />
                            );
                        }
                    },
                    {
                        title: 'Customer',
                        field: 'customer_name',
                        sorting: false,
                        hidden: !permissions.includes('internal_clinical_services'),
                        render: (rowData: any) => {
                            if (!rowData.customer_name) {
                                return '';
                            }
                            return (
                                <Typography variant="body2" color="textSecondary">
                                    {rowData.customer_name}
                                </Typography>
                            );
                        }
                    },
                    {
                        title: 'Programme',
                        field: 'programme_name',
                        sorting: false,
                        render: (rowData: any) => {
                            if (!rowData.programme_name) {
                                return '';
                            }
                            return (
                                <Typography variant="body2" color="textSecondary">
                                    {rowData.programme_name}
                                </Typography>
                            );
                        }
                    },
                    {
                        title: 'Use Case',
                        field: 'customer_use_case',
                        sorting: false,
                        hidden: !permissions.includes('internal_clinical_services'),
                        render: (rowData: any) => {
                            if (!rowData.customer_preset) {
                                return '-';
                            }
                            return (
                                <Chip
                                    label={rowData.customer_preset.split('_').join(' ')}
                                    size="small"
                                    sx={{
                                        textTransform: 'capitalize',
                                        border: `2px solid ${
                                            USE_CASE_COLORS[`${rowData.customer_preset}`]
                                        }`
                                    }}
                                    variant="outlined"
                                />
                            );
                        }
                    },
                    {
                        title: 'Status',
                        field: 'case_status',
                        sorting: false,
                        render: (rowData: any) => {
                            if (!rowData.case_status) {
                                return '';
                            }
                            return (
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ textTransform: 'capitalize' }}
                                >
                                    {rowData.case_status.split('_').join(' ')}
                                </Typography>
                            );
                        }
                    },
                    {
                        title: 'Last Active',
                        field: 'last_active',
                        sorting: false,
                        render: (rowData: any) => {
                            if (!rowData.last_active) {
                                return '';
                            }
                            return (
                                <Typography variant="body2" color="textSecondary">
                                    {rowData.last_active.toDate().toLocaleDateString('en-GB', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </Typography>
                            );
                        }
                    }
                ]}
                options={{
                    pageSize: 20,
                    pageSizeOptions: [20, 40, 60],
                    searchFieldVariant: 'outlined',
                    filtering: false,
                    showFirstLastPageButtons: false,
                    paging: true
                }}
                components={{
                    Toolbar: (props) => (
                        <Box m={1}>
                            <MTableToolbar {...props} />
                            <Container maxWidth="xl">
                                <Grid container spacing={2} marginTop={1}>
                                    {permissions.includes('internal_clinical_services') && (
                                        <Grid size={{ xs: 6, lg: 3 }}>
                                            <MultiFilter
                                                value={clientFilter ? clientFilter : []}
                                                options={filterList.map((c) => ({
                                                    id: c.id,
                                                    name: c.name
                                                }))}
                                                onChange={setClientFilter}
                                                label="Customer"
                                            />
                                        </Grid>
                                    )}
                                    {permissions.includes('internal_clinical_services') && (
                                        <Grid size={{ xs: 6, lg: 3 }}>
                                            <MultiFilter
                                                value={useCaseFilter}
                                                options={USE_CASE_LIST}
                                                onChange={setUseCaseFilter}
                                                label="Use Case"
                                            />
                                        </Grid>
                                    )}
                                    <Grid size={{ xs: 6, lg: 3 }}>
                                        <MultiFilter
                                            value={pendingTaskFilter}
                                            options={[
                                                { id: 'all', name: 'All' },
                                                {
                                                    id: 'thread_open',
                                                    name: 'Open chat thread'
                                                },
                                                {
                                                    id: 'scheduled_review',
                                                    name: 'Scheduled review'
                                                },
                                                {
                                                    id: 'unread_messages_count',
                                                    name: 'Unread messages'
                                                }
                                            ]}
                                            onChange={setPendingTaskFilter}
                                            label="Pending Tasks"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6, lg: 3 }}>
                                        <Filter
                                            value={taskOwnerFilter}
                                            options={[
                                                {
                                                    id: clinician ? clinician.id : '',
                                                    name: 'Assigned to me'
                                                },
                                                { id: 'all', name: 'All' }
                                            ]}
                                            onChange={setTaskOwnerFilter}
                                            label="Task Owner"
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </Container>
                        </Box>
                    )
                }}
                actions={[
                    (rowData: any) => ({
                        icon: 'visibility',
                        tooltip: 'View Patient',
                        onClick: () => {
                            // add event to log view patient from worklist
                            logGoogleAnalyticsEvent('clinical_worklist_view_patient_click', {
                                logged_in_user_id: clinician.id,
                                logged_in_user_role: clinician.role,
                                logged_in_username: clinician.name,
                                customer_id: rowData.customer_id,
                                customer_name: rowData.customer_name,
                                programme_name: rowData.programme_name,
                                region: rowData.region
                            });
                            navigate(`/dashboard/phioengage-profile/${rowData.id}`);
                            setUserActivity({
                                patientId: rowData.id,
                                eventName: 'details_screen_visited',
                                eventDetails: { visited_from: 'clinical_worklist' }
                            });
                        }
                    })
                ]}
                localization={{
                    toolbar: {
                        searchPlaceholder: 'UID, First/Last name, email, Customer, Programme'
                    },
                    body: {
                        emptyDataSourceMessage: 'No patients currently require action'
                    },
                    pagination: {
                        labelRowsPerPage: '',
                        labelDisplayedRows: ''
                    }
                }}
            />
        </Box>
    );
};

export default PhioengageWorklist;
